import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Upload, Loader2, ShieldCheck } from "lucide-react";
import AppShell from "@/components/app/AppShell";
import api, { apiErrorMessage } from "@/lib/api";

const field = "w-full border border-[#E7E5DF] bg-white px-3.5 py-2.5 text-[15px] text-[#181E4B] rounded-md outline-none focus:border-[#181E4B]";
const label = "mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-[#5E6282]";
const STEPS = ["Destination", "Visa type", "Passport", "Confirm"];

const Row = ({ title, sub, onClick, disabled, testid }) => (
  <button data-testid={testid} disabled={disabled} onClick={onClick}
    className={`flex w-full items-center justify-between rounded-md border border-[#E7E5DF] bg-white px-5 py-4 text-left transition-colors ${disabled ? "cursor-not-allowed opacity-50" : "hover:border-[#181E4B]"}`}>
    <span><span className="block font-semibold text-[#1E1D4C]">{title}</span>{sub && <span className="mt-0.5 block text-sm text-[#5E6282]">{sub}</span>}</span>
    {!disabled && <ArrowRight size={16} className="text-[#5E6282]" />}
  </button>
);

export default function NewCase() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [step, setStep] = useState(1);
  const [corridors, setCorridors] = useState([]);
  const [corridor, setCorridor] = useState(null);
  const [visa, setVisa] = useState(null);
  const [passportFile, setPassportFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractStatus, setExtractStatus] = useState(null);
  const [profile, setProfile] = useState({ full_name: "", date_of_birth: "", nationality: "", passport_number: "", passport_expiry: "" });
  const [province, setProvince] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/corridors").then((r) => setCorridors(r.data)).catch(() => {}); }, []);

  const jurisdictions = corridor?.jurisdictions || [];
  const provinces = jurisdictions.flatMap((j) => j.provinces);
  const mission = jurisdictions.find((j) => j.provinces.includes(province))?.mission;

  const onPassport = async (file) => {
    if (!file) return;
    setPassportFile(file); setExtracting(true); setError("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/documents/extract-passport", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, ...Object.fromEntries(Object.entries(data.profile).filter(([, v]) => v)) }));
      setExtractStatus(data.status);
    } catch (e) {
      setExtractStatus("Failed");
      setError(apiErrorMessage(e.response?.data?.detail) || "We could not read the passport automatically. Please enter the details below.");
    } finally { setExtracting(false); setStep(4); }
  };

  const submit = async () => {
    setError(""); setBusy(true);
    try {
      const { data: created } = await api.post("/cases", {
        corridor_key: corridor.key, visa_category: visa.key, profile,
        conditions: province ? { residence_province: province } : undefined,
      });
      if (passportFile) {
        try {
          const { data: full } = await api.get(`/cases/${created.id}`);
          const passReq = (full.checklist || []).find((r) => (r.evidence_types || []).includes("passport")) || (full.checklist || []).find((r) => r.code.includes("passport"));
          if (passReq) {
            const fd = new FormData();
            fd.append("case_id", created.id); fd.append("doc_type", passReq.code); fd.append("file", passportFile);
            await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
          }
        } catch { /* non-blocking */ }
      }
      navigate(`/app/cases/${created.id}`);
    } catch (e) {
      setError(apiErrorMessage(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const heading = "font-serif text-2xl font-bold text-[#181E4B] sm:text-3xl";
  const back = (to, tid, text) => <button onClick={() => setStep(to)} className="inline-flex items-center gap-1.5 text-sm text-[#5E6282] hover:text-[#181E4B]" data-testid={tid}><ArrowLeft size={14} /> {text}</button>;

  return (
    <AppShell>
      <div className="mx-auto max-w-xl py-4">
        <div className="mb-8 border-b border-[#E7E5DF] pb-4" data-testid="wizard-steps">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#5E6282]">Step {step} of 4</p>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
            {STEPS.map((s, i) => (
              <span key={s} className={`text-sm ${step === i + 1 ? "font-semibold text-[#181E4B]" : step > i + 1 ? "text-[#181E4B]" : "text-[#5E6282]/60"}`}>
                {step > i + 1 && <Check size={13} className="mr-1 inline" />}{s}
              </span>
            ))}
          </div>
        </div>

        {step === 1 && (
          <div data-testid="step-destination">
            <h2 className={heading}>Where are you going?</h2>
            <p className="mb-6 mt-2 text-[#5E6282]">Choose your destination.</p>
            <div className="space-y-3">
              {corridors.map((c) => {
                const off = c.availability !== "Available";
                return <Row key={c.key} testid={`pick-corridor-${c.key}`} title={`${c.flag || ""} ${c.destination}`} sub={off ? "Coming soon" : "Service available"} disabled={off} onClick={() => { setCorridor(c); setVisa(null); setProvince(""); setStep(2); }} />;
              })}
            </div>
          </div>
        )}

        {step === 2 && corridor && (
          <div data-testid="step-visa">
            <h2 className={heading}>Which visa for {corridor.destination}?</h2>
            <p className="mb-6 mt-2 text-[#5E6282]">Pick the type that matches your trip.</p>
            <div className="space-y-3">
              {corridor.visa_categories.map((v) => <Row key={v.key} testid={`pick-visa-${v.key}`} title={v.name} sub={v.available ? null : "Coming soon"} disabled={!v.available} onClick={() => { setVisa(v); setStep(3); }} />)}
            </div>
            <div className="mt-6">{back(1, "back-to-destination", "Change destination")}</div>
          </div>
        )}

        {step === 3 && visa && (
          <div data-testid="step-passport">
            <h2 className={heading}>Upload your passport</h2>
            <p className="mt-2 text-[#5E6282]">We read your details from the photo page, so you do not have to type them. Photo or PDF.</p>
            <button type="button" onClick={() => !extracting && fileRef.current?.click()} disabled={extracting} data-testid="passport-dropzone"
              className="mt-6 flex w-full flex-col items-center gap-3 rounded-md border border-dashed border-[#181E4B] bg-white px-6 py-12 text-center hover:bg-[#FFF1DA]/40">
              {extracting ? (<><Loader2 size={24} className="animate-spin text-[#181E4B]" /><span className="font-medium text-[#181E4B]">Reading your passport…</span></>)
                : (<><Upload size={22} className="text-[#181E4B]" /><span className="font-medium text-[#181E4B]">Tap to upload your passport</span><span className="text-xs text-[#5E6282]">JPG, PNG or PDF, up to 15 MB</span></>)}
            </button>
            <input ref={fileRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={(e) => onPassport(e.target.files[0])} data-testid="passport-input" />
            <div className="mt-4 flex items-start gap-2 text-xs text-[#5E6282]"><ShieldCheck size={15} className="mt-0.5 shrink-0" /><span>Your passport is stored privately and used only to prepare your application.</span></div>
            <div className="mt-6 flex items-center justify-between">
              {back(2, "back-to-visa", "Change visa type")}
              <button onClick={() => { setPassportFile(null); setExtractStatus(null); setStep(4); }} className="text-sm text-[#5E6282] underline hover:text-[#181E4B]" data-testid="skip-passport">Prefer typing? Enter the details by hand</button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid="step-confirm">
            <h2 className={heading}>Confirm your application</h2>
            <p className="mb-6 mt-2 text-[#5E6282]">Check these details before we open your guided chat.</p>
            <dl className="divide-y divide-[#E7E5DF] rounded-md border border-[#E7E5DF] bg-white">
              {[["Destination", corridor?.destination], ["Visa type", visa?.name], ...(mission ? [["Deciding mission", mission]] : [])].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 px-5 py-3.5"><dt className="text-sm text-[#5E6282]">{k}</dt><dd className="text-right text-sm font-semibold text-[#1E1D4C]" data-testid={`confirm-${k.toLowerCase().replace(/ /g, "-")}`}>{v}</dd></div>
              ))}
            </dl>
            {extractStatus === "Extracted" && <p className="mt-4 text-sm text-[#15803D]" data-testid="extract-success">Details read from your passport. Please check them.</p>}
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {provinces.length > 0 && (
                <div className="sm:col-span-2">
                  <label className={label} htmlFor="prov">Province where you live</label>
                  <select id="prov" className={field} value={province} onChange={(e) => setProvince(e.target.value)} data-testid="profile-province"><option value="">Select</option>{provinces.map((p) => <option key={p}>{p}</option>)}</select>
                </div>
              )}
              <div className="sm:col-span-2"><label className={label} htmlFor="fn">Full name (as on passport)</label><input id="fn" className={field} value={profile.full_name} onChange={(e) => setProfile({ ...profile, full_name: e.target.value })} data-testid="profile-full-name" /></div>
              <div><label className={label} htmlFor="dob">Date of birth</label><input id="dob" type="date" className={field} value={profile.date_of_birth} onChange={(e) => setProfile({ ...profile, date_of_birth: e.target.value })} data-testid="profile-dob" /></div>
              <div><label className={label} htmlFor="nat">Nationality</label><input id="nat" className={field} value={profile.nationality} onChange={(e) => setProfile({ ...profile, nationality: e.target.value })} data-testid="profile-nationality" /></div>
              <div><label className={label} htmlFor="pn">Passport number</label><input id="pn" className={field} value={profile.passport_number} onChange={(e) => setProfile({ ...profile, passport_number: e.target.value })} data-testid="profile-passport" /></div>
              <div><label className={label} htmlFor="pe">Passport expiry</label><input id="pe" type="date" className={field} value={profile.passport_expiry} onChange={(e) => setProfile({ ...profile, passport_expiry: e.target.value })} data-testid="profile-passport-expiry" /></div>
            </div>
            {error && <p className="mt-4 rounded-md border border-coral/30 bg-coral/5 px-3.5 py-2.5 text-sm text-coral" role="alert" data-testid="newcase-error">{error}</p>}
            <div className="mt-8 flex items-center justify-between gap-3">
              {back(3, "back-to-passport", "Back")}
              <button onClick={submit} disabled={busy || !profile.full_name} data-testid="create-case-submit"
                className="inline-flex items-center gap-2 rounded-md bg-[#F1A501] px-6 py-3 text-sm font-semibold text-[#14183E] hover:bg-[#D99300] disabled:opacity-50">
                {busy ? <><Loader2 size={15} className="animate-spin" /> Creating</> : "Start my application"}
              </button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
